import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function UltimateGuideStanleyPark() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "ultimate-guide-stanley-park";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "must-see-attractions": "Must-See Attractions & Hidden Gems",
    "hiking-outdoor": "Hiking Trails & Outdoor Activities",
    "family-friendly": "Family-Friendly Activities",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/stanley-park.aspx",
      label: "City of Vancouver – Stanley Park",
    },
    {
      url: "https://stanleyparkecology.ca/",
      label: "Stanley Park Ecology Society",
    },
    {
      url: "https://www.destinationvancouver.com/vancouver/places-to-see/stanley-park/",
      label: "Destination Vancouver – Stanley Park Guide",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" must match the ref used in the corresponding <p ref=...>
  // Coordinates go in `lat` / `lng`.
  //
  // NOTE: From the conversation, we've got multiple spots with approximate coords:
  // Vancouver Aquarium: 49.30059, -123.13105
  // Brockton Point Totem Poles: 49.29929, -123.12103
  // Prospect Point: 49.31356, -123.14239
  // Siwash Rock: 49.30878, -123.15606
  // Lost Lagoon: 49.29595, -123.14056
  // Beaver Lake: 49.30389, -123.13889
  // Rose Garden: 49.29899, -123.13590
  // Hollow Tree: 49.30691, -123.15361
  // “Girl in a Wetsuit” statue: 49.30262, -123.12593
  //
  // We'll map them to relevant sections. Let's group them under "must-see-attractions".
  const locations = [
    {
      id: "aquarium",
      lat: 49.30059,
      lng: -123.13105,
      popupText: "Vancouver Aquarium",
    },
    {
      id: "totem-poles",
      lat: 49.29929,
      lng: -123.12103,
      popupText: "Brockton Point Totem Poles",
    },
    {
      id: "prospect-point",
      lat: 49.31356,
      lng: -123.14239,
      popupText: "Prospect Point",
    },
    {
      id: "siwash-rock",
      lat: 49.30878,
      lng: -123.15606,
      popupText: "Siwash Rock",
    },
    {
      id: "lost-lagoon",
      lat: 49.29595,
      lng: -123.14056,
      popupText: "Lost Lagoon",
    },
    {
      id: "beaver-lake",
      lat: 49.30389,
      lng: -123.13889,
      popupText: "Beaver Lake",
    },
    {
      id: "rose-garden",
      lat: 49.29899,
      lng: -123.13590,
      popupText: "Stanley Park Rose Garden",
    },
    {
      id: "hollow-tree",
      lat: 49.30691,
      lng: -123.15361,
      popupText: "Hollow Tree",
    },
    {
      id: "girl-wetsuit",
      lat: 49.30262,
      lng: -123.12593,
      popupText: "Girl in a Wetsuit Statue",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Stanley Park: Must-See Attractions, Hidden Gems & Pro Tips</title>
        <meta
          name="description"
          content="A comprehensive guide to exploring Vancouver's Stanley Park. Discover must-see attractions, hidden gems, hiking trails, family activities, local regulations, FAQs, and more."
        />
        <meta
          name="keywords"
          content="Stanley Park, Vancouver, travel tips, attractions, hidden gems, hiking, family-friendly activities"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/ultimate-guide-stanley-park.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Stanley Park: Must-See Attractions, Hidden Gems & Pro Tips"
        />
        <meta
          property="og:description"
          content="An in-depth guide covering the best of Vancouver's Stanley Park: iconic landmarks, hidden gems, hiking trails, and practical visitor tips."
        />
        <meta
          property="og:image"
          content="https://www.bcouver.com/assets/images/ultimate-guide-stanley-park.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/ultimate-guide-stanley-park.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Stanley Park: Must-See Attractions, Hidden Gems & Pro Tips"
        />
        <meta
          name="twitter:description"
          content="Explore Stanley Park in Vancouver with this complete visitor guide: attractions, hidden gems, trails, regulations, and more."
        />
        <meta
          name="twitter:image"
          content="https://www.bcouver.com/assets/images/ultimate-guide-stanley-park.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://www.bcouver.com/assets/images/ultimate-guide-stanley-park.jpg"
              }
              alt="Stanley Park's lush forest and the Pacific Ocean in the background"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              <strong>Stanley Park</strong> is Vancouver’s iconic 405-hectare urban oasis.
              Established in 1888, it remains the city’s first and largest park, a lush
              peninsula of towering trees, scenic waterfront views, and cultural
              landmarks. Bordered by the Pacific Ocean and just minutes from downtown,
              Stanley Park welcomes millions of visitors every year. Whether you’re a
              local or a traveler, the park offers an incredible blend of nature,
              recreation, and heritage — from forest hikes and beaches to historic
              monuments and wildlife encounters.
            </p>

            {/* SECTION: Must-See Attractions & Hidden Gems */}
            <h2 id="must-see-attractions">Must-See Attractions &amp; Hidden Gems</h2>
            <p>
              Stanley Park is home to world-class attractions and quieter corners waiting
              to be discovered. Below are the top spots you won’t want to miss, complete
              with coordinates to help you plan your route.
            </p>

            {/* Vancouver Aquarium */}
            <p
              ref={(el) => setSpotRef("aquarium", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Aquarium</strong>{" "}
              <Marker
                coordY={49.30059}
                coordX={-123.13105}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– Canada's largest aquarium, boasting over 65,000 marine animals,
              interactive exhibits, and a renowned marine mammal rescue program. Paid
              admission is required, but it’s a highlight for families and conservation
              enthusiasts. Typically open daily from 9:30 AM – 5:00 PM, with extended
              summer hours.
            </p>

            {/* Brockton Point Totem Poles */}
            <p
              ref={(el) => setSpotRef("totem-poles", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Brockton Point Totem Poles</strong>{" "}
              <Marker
                coordY={49.29929}
                coordX={-123.12103}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– The most visited attraction in BC, these carved poles from various
              First Nations are a striking reminder of the region's Indigenous heritage.
              Located near the park’s eastern tip, they’re accessible 24/7 with no
              admission fee.
            </p>

            {/* Prospect Point */}
            <p
              ref={(el) => setSpotRef("prospect-point", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Prospect Point</strong>{" "}
              <Marker
                coordY={49.31356}
                coordX={-123.14239}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– The highest lookout in Stanley Park, offering panoramic views of
              the Lion’s Gate Bridge, Burrard Inlet, and North Shore mountains. There’s a
              café and gift shop on site; parking is available if you’re driving.
            </p>

            {/* Siwash Rock */}
            <p
              ref={(el) => setSpotRef("siwash-rock", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Siwash Rock</strong>{" "}
              <Marker
                coordY={49.30878}
                coordX={-123.15606}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– A 32-million-year-old basalt sea stack crowned by a lone tree,
              visible from the seawall between Third Beach and the Lions Gate Bridge.
              Steeped in Coast Salish legend, it's an iconic photo spot.
            </p>

            {/* Lost Lagoon */}
            <p
              ref={(el) => setSpotRef("lost-lagoon", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Lost Lagoon</strong>{" "}
              <Marker
                coordY={49.29595}
                coordX={-123.14056}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– A tranquil freshwater lake at the park’s entrance, home to a variety
              of birds (swans, geese, ducks), plus the Stanley Park Nature House. Enjoy a
              flat 1.75 km loop trail around the lagoon or relax by the Jubilee Fountain.
            </p>

            {/* Beaver Lake */}
            <p
              ref={(el) => setSpotRef("beaver-lake", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Beaver Lake</strong>{" "}
              <Marker
                coordY={49.30389}
                coordX={-123.13889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– A peaceful hidden gem in the heart of the forest. Surrounded by
              wetlands, this shallow lake is an ideal spot for bird-watching and a serene
              1.0 km loop trail.
            </p>

            {/* Stanley Park Rose Garden */}
            <p
              ref={(el) => setSpotRef("rose-garden", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park Rose Garden</strong>{" "}
              <Marker
                coordY={49.29899}
                coordX={-123.13590}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– Near the southeastern corner, this manicured garden features over
              3,500 rose bushes, an arbored walkway, and blooms from spring through
              autumn. Adjacent is the Tudor-style Stanley Park Pavilion.
            </p>

            {/* Hollow Tree */}
            <p
              ref={(el) => setSpotRef("hollow-tree", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Hollow Tree</strong>{" "}
              <Marker
                coordY={49.30691}
                coordX={-123.15361}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– A massive stump of an ancient Western red cedar, estimated at 700–
              800 years old. Once a famous photo spot for early visitors (and even an
              elephant!), it’s now stabilized to preserve the historic remnant.
            </p>

            {/* Girl in a Wetsuit */}
            <p
              ref={(el) => setSpotRef("girl-wetsuit", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>“Girl in a Wetsuit” Statue</strong>{" "}
              <Marker
                coordY={49.30262}
                coordX={-123.12593}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;– A bronze sculpture perched on a rock off the north side of the
              seawall, reminiscent of Copenhagen’s Little Mermaid. Created in 1972 by
              Elek Imredy, it celebrates Vancouver’s maritime spirit.
            </p>

            {/* SECTION: Hiking Trails & Outdoor Activities */}
            <h2 id="hiking-outdoor">Hiking Trails &amp; Outdoor Activities</h2>
            <p>
              Whether you want a casual stroll or a challenging climb, Stanley Park’s
              trails and amenities cater to all fitness levels. 
            </p>
            <ul>
              <li>
                <strong>Seawall Loop (Easy, 9 km)</strong> – A scenic 9–10 km paved path
                around the park’s edge. Walkers and cyclists can enjoy sweeping
                ocean-and-mountain views. Cycling is one-way (counter-clockwise), and
                walking typically on a separate path.
              </li>
              <li>
                <strong>Forest Trails</strong> – Over 27 km of interior trails for
                forested hikes. Short loops like <em>Beaver Lake Trail</em> or deeper
                walks like <em>Cathedral Trail</em> let you experience towering cedars and
                quiet nature.
              </li>
              <li>
                <strong>Prospect Point Trail (Moderate)</strong> – A short but steep
                trail from the seawall up to Prospect Point (~70 m elevation gain). Great
                workout and stunning views at the top.
              </li>
              <li>
                <strong>Beaches</strong> – Second Beach and Third Beach invite swimming,
                sunbathing, and sunset watching. Second Beach Pool is a heated outdoor
                pool (summer-only), ideal for families.
              </li>
              <li>
                <strong>Cycling &amp; Rollerblading</strong> – Bike rentals abound near
                the park entrance (from ~$10/hour). Helmets are mandatory; follow posted
                signs to ensure a safe ride. Rollerbladers share the bike lane.
              </li>
              <li>
                <strong>Pitch &amp; Putt, Tennis, &amp; More</strong> – The park has
                public tennis courts, an 18-hole pitch-and-putt golf course, open lawns
                for soccer or frisbee, plus running loops for fitness enthusiasts.
              </li>
            </ul>

            {/* SECTION: Family-Friendly Activities */}
            <h2 id="family-friendly">Family-Friendly Activities</h2>
            <p>
              Stanley Park offers plenty of fun for kids. The <strong>Miniature Train</strong> runs seasonally,
              featuring special events at Halloween and Christmas. Second Beach’s
              playground, outdoor pool, and the <strong>Lumbermen’s Arch Splash Park</strong> (open
              in summer) provide safe and exciting spaces for children to play. Visiting
              the <strong>Vancouver Aquarium</strong> is also a surefire crowd-pleaser, offering
              4D films, hands-on exhibits, and daily animal feedings.
            </p>
            <p>
              For a more relaxed outing, stroll around <strong>Beaver Lake</strong>, watch for
              ducks or turtles, or take a horse-drawn carriage tour from the park’s
              entrance. There’s something here for families of all sizes and budgets.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations &amp; Best Practices</h2>
            <p>
              Stanley Park is a protected environment with rules to keep everyone safe
              and to preserve its natural beauty:
            </p>
            <ul>
              <li>
                <strong>No Smoking or Vaping:</strong> All Vancouver parks prohibit
                smoking/vaping.
              </li>
              <li>
                <strong>Do Not Feed Wildlife:</strong> Feeding animals is illegal and
                harmful to them (and you). Fines can apply.
              </li>
              <li>
                <strong>Stay on Marked Trails:</strong> Helps protect vegetation and
                prevent erosion.
              </li>
              <li>
                <strong>Leash Rules for Dogs:</strong> Dogs must be on-leash except in
                designated off-leash areas.
              </li>
              <li>
                <strong>Pay Parking:</strong> Parking is metered (~$3.50/hr or
                $14–15/day). Ticket enforcement is active.
              </li>
              <li>
                <strong>Beach Safety:</strong> Swim in supervised areas only during
                lifeguard hours (summer). Watch out for cold water temperatures and
                changing tides.
              </li>
            </ul>
            <p>
              Be mindful of potential wildlife sightings (raccoons, coyotes) and respect
              closure signs after windstorms. Always check for official updates on
              closures or events.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need to pay to enter Stanley Park?</h3>
              <p>
                No. The park is free to enter, and you can walk, bike, or drive in at no
                charge. Only specific attractions (like the Aquarium) or parking require
                payment.
              </p>

              <h3>2. How long is the seawall, and can I bike it?</h3>
              <p>
                The seawall is roughly 9 km around Stanley Park. It takes about 2–3 hours
                to walk the full loop or ~1 hour to cycle. Cyclists must follow the
                one-way (counter-clockwise) direction and yield to pedestrians where
                paths merge.
              </p>

              <h3>3. When is the best time of year to visit?</h3>
              <p>
                Summer is peak season for warm weather and extended hours, but spring and
                autumn also offer mild conditions and fewer crowds. Winter can be rainy
                but serene. Each season has its own charm.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Stanley Park is truly one of Vancouver’s crown jewels, offering a perfect
              balance of natural beauty, recreation, and cultural heritage. Plan a day
              (or several) to explore its iconic attractions, hidden forest trails, and
              stunning beaches. Whether you visit for an afternoon picnic or a
              dawn-to-dusk adventure, you’ll find endless ways to immerse yourself in the
              park’s lush coastal charm.
            </p>
            <p>
              <strong>Disclaimer:</strong> Always check official resources for the latest
              updates on park regulations, closures, or safety advisories. While Stanley
              Park is generally safe, exercise caution if venturing off the main paths or
              visiting at night. Follow local bylaws and respect the wildlife to ensure
              an enjoyable, responsible visit.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}